import { getDepictProductsInListingViaExternalIdClient } from "@/actions/categories/get-depict-products-in-listing-via-external-id-client"
import type { ProductGroupItem } from "@/app/(site)/_components/Organisms/ProductGrids/ProductGridContent"
import { transformedFilterOptions } from "@/app/(site)/_components/Organisms/ProductGrids/helpers"
import type { VariantSortPreference } from "@/app/(site)/_utils/seasonalMerchandising"
import { type Locale } from "@/i18n/locales"
import type { CurrencyFormat } from "@/lib/depict-client/helpers/depict-transforms"
import type { SortModel } from "@/lib/depict-client/types/api/ProductListingRequestV3"
import logger from "@/lib/logger/logger-init"
import { useEffect, useMemo, useRef, useState } from "react"
import useSWRInfinite from "swr/infinite"

export type FetchProductsParams = {
    externalId: string | undefined
    marketCode: string
    localeCode: string
    depictSessionId: string
    filters: any[]
    limit: number
    sort: SortModel | undefined
    cursor: string | undefined
    sortPreference: VariantSortPreference | undefined
}

export type InfinitePageData = {
    products: ProductGroupItem[]
    cursor: string | null
    nHits?: number
}

type InfiniteListingKey = {
    type: "infinite-listing"
    externalId: string | undefined
    marketCode: string
    localeCode: string
    depictSessionId: string
    limit: number
    sortPreference: VariantSortPreference | undefined
    filters: any[]
    sort: SortModel | undefined
    pageIndex: number
    cursor: string | undefined
}

export const useSWRInfiniteProductListing = (
    baseParams: Omit<FetchProductsParams, "cursor">,
    basketSessionCurrencyFormat: CurrencyFormat | undefined,
    paramsLocale: Locale,
    fallbackData: InfinitePageData[],
    currentPage: number,
    totalPages: number,
    generalSizeToSizeMap?: Record<string, string[]>
) => {
    const [activeStartPage, setActiveStartPage] = useState(currentPage)
    const activeStartRef = useRef(activeStartPage)
    activeStartRef.current = activeStartPage

    // Server-provided cursors maintain the key chain even when
    // the fetcher returns null data for pages before activeStart.
    const serverCursorsRef = useRef(fallbackData.map(p => p.cursor))

    // When filters/sort change, reset to page 1 with a fresh fetch.
    const hasFiltersOrSort =
        baseParams.filters.length > 0 || baseParams.sort !== undefined
    const filterSortKey = JSON.stringify({
        filters: baseParams.filters,
        sort: baseParams.sort
    })
    const filterSortKeyRef = useRef(filterSortKey)

    const { data, size, setSize, isValidating, mutate } = useSWRInfinite<
        InfinitePageData,
        Error,
        (
            pageIndex: number,
            previousPageData: InfinitePageData | null
        ) => InfiniteListingKey | null
    >(
        (
            pageIndex: number,
            previousPageData: InfinitePageData | null
        ): InfiniteListingKey | null => {
            if (
                previousPageData &&
                !previousPageData.cursor &&
                previousPageData.products.length > 0
            ) {
                return null
            }

            return {
                type: "infinite-listing" as const,
                ...baseParams,
                pageIndex,
                cursor:
                    pageIndex === 0
                        ? undefined
                        : previousPageData?.cursor ||
                          serverCursorsRef.current[pageIndex - 1] ||
                          undefined
            }
        },
        async (key: InfiniteListingKey): Promise<InfinitePageData> => {
            if (key.pageIndex < activeStartRef.current - 1) {
                return { products: [], cursor: null }
            }

            try {
                const response: any = await getDepictProductsInListingViaExternalIdClient(
                    {
                        market: baseParams.marketCode,
                        locale: baseParams.localeCode,
                        external_id: baseParams.externalId || "",
                        session_id: baseParams.depictSessionId,
                        limit: baseParams.limit,
                        cursor: key.cursor || undefined,
                        filters: transformedFilterOptions(
                            baseParams.filters,
                            generalSizeToSizeMap
                        ),
                        sort:
                            baseParams.sort && baseParams.sort.field !== "_relevance"
                                ? baseParams.sort
                                : undefined,
                        basketSessionCurrencyFormat,
                        paramsLocale,
                        sortPreference: baseParams.sortPreference
                    }
                )
                return {
                    products: response?.products ?? [],
                    cursor: response?.cursor || null,
                    nHits: response?.n_hits ?? 0
                }
            } catch (error) {
                logger.error("Error fetching products:", error)
                throw error
            }
        },
        {
            fallbackData: hasFiltersOrSort ? undefined : fallbackData,
            initialSize: hasFiltersOrSort ? 1 : fallbackData.length,
            revalidateFirstPage: false,
            revalidateAll: false,
            revalidateOnFocus: false,
            revalidateOnMount: true
        }
    )

    useEffect(() => {
        if (filterSortKeyRef.current !== filterSortKey) {
            filterSortKeyRef.current = filterSortKey
            activeStartRef.current = 1
            setActiveStartPage(1)
            setSize(1)
            mutate(undefined, { revalidate: true })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterSortKey])

    const pages = data ?? fallbackData
    const lastPage = pages[pages.length - 1]
    const clientNHits = data?.[0]?.nHits
    const effectiveTotalPages =
        clientNHits !== undefined ? Math.ceil(clientNHits / baseParams.limit) : totalPages
    const hasMorePages = size < effectiveTotalPages && lastPage?.cursor !== null
    const hasPreviousPages = activeStartPage > 1

    const visiblePageProducts = useMemo(
        () => pages.slice(activeStartPage - 1).map(p => p?.products ?? []),
        [pages, activeStartPage]
    )

    const products = useMemo(() => visiblePageProducts.flat(), [visiblePageProducts])

    const loadMoreSWR = () => {
        if (!hasMorePages) return null
        const newSize = size + 1
        setSize(newSize)
        return newSize
    }

    const loadPreviousSWR = async () => {
        if (activeStartPage <= 1) return null
        const newStart = activeStartPage - 1
        const targetIndex = newStart - 1

        activeStartRef.current = newStart
        setActiveStartPage(newStart)

        await mutate(data => data, {
            revalidate: (_pageData, key) => {
                return (key as InfiniteListingKey | null)?.pageIndex === targetIndex
            }
        })

        return newStart
    }

    const syncToServerPage = (serverPage: number, serverFallback: InfinitePageData[]) => {
        setActiveStartPage(serverPage)
        setSize(serverFallback.length)
        mutate(serverFallback, { revalidate: false })
        serverCursorsRef.current = serverFallback.map(p => p.cursor)
    }

    return {
        pages,
        products,
        visiblePageProducts,
        activeStartPage,
        hasMorePages,
        hasPreviousPages,
        size,
        isValidating,
        loadMoreSWR,
        loadPreviousSWR,
        syncToServerPage,
        setSize,
        clientNHits
    }
}
